var React = require('react');
var { createElement: h, useCallback, useEffect, useMemo, useRef, useState } = React;
var { fallbackSizes } = require('../lib/canvasUtils');

function isVariablesNode(node) { return !!(node && node.kind === 'workflow-node' && node.icon === 'variables'); }

var MINI_MAP_PADDING = 10;

var iconColorMap = {
  start: '#7ee8c8', agent: '#7f96b8', data: '#60b8f4', validation: '#a8b8ff',
  sync: '#68d9b0', condition: '#f5c842', variables: '#6f8fb2', 'set-variable': '#6f8fb2',
  'console-log': '#b890e8', classifier: '#f09060', sleep: '#80b8f0', loop: '#60d8c0',
  'file-preview': '#88b8f8', group: '#64b4ff', custom: '#d7dce4', 'on-fail': '#f08080'
};

function getMiniMapNodeRect(node, rectById) {
  if (!node || !node.id) return null;
  if (rectById && rectById[node.id]) return rectById[node.id];

  var position = node.position || {};
  var size = node.size || fallbackSizes[node.id] || { width: 220, height: 80 };
  var left = Number.isFinite(position.left) ? position.left : 0;
  var top = Number.isFinite(position.top) ? position.top : 0;
  var width = Number.isFinite(size.width) ? size.width : 220;
  var height = Number.isFinite(size.height) ? size.height : 80;

  return {
    left: left,
    top: top,
    right: left + width,
    bottom: top + height,
    width: width,
    height: height
  };
}

function buildMiniMapLayout(nodes, rectById, boxWidth, boxHeight, viewportBounds) {
  var visibleNodes = [];
  (nodes || []).forEach(function(node) {
    if (!node || !node.id || isVariablesNode(node)) return;
    var rect = getMiniMapNodeRect(node, rectById);
    if (!rect) return;
    visibleNodes.push({
      id: node.id,
      left: rect.left,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      icon: node.icon || 'custom'
    });
  });

  if (!visibleNodes.length) return null;

  var minLeft = Infinity;
  var minTop = Infinity;
  var maxRight = -Infinity;
  var maxBottom = -Infinity;

  visibleNodes.forEach(function(rect) {
    minLeft = Math.min(minLeft, rect.left);
    minTop = Math.min(minTop, rect.top);
    maxRight = Math.max(maxRight, rect.right);
    maxBottom = Math.max(maxBottom, rect.bottom);
  });

  // Include the viewport bounds so the viewport rect never overflows
  if (viewportBounds) {
    minLeft = Math.min(minLeft, viewportBounds.left);
    minTop = Math.min(minTop, viewportBounds.top);
    maxRight = Math.max(maxRight, viewportBounds.right);
    maxBottom = Math.max(maxBottom, viewportBounds.bottom);
  }

  var contentWidth = Math.max(1, maxRight - minLeft);
  var contentHeight = Math.max(1, maxBottom - minTop);
  var innerWidth = Math.max(1, boxWidth - (MINI_MAP_PADDING * 2));
  var innerHeight = Math.max(1, boxHeight - (MINI_MAP_PADDING * 2));
  var scale = Math.min(innerWidth / contentWidth, innerHeight / contentHeight);
  if (!Number.isFinite(scale) || scale <= 0) scale = 1;

  var scaledWidth = contentWidth * scale;
  var scaledHeight = contentHeight * scale;
  var originX = MINI_MAP_PADDING + ((innerWidth - scaledWidth) / 2);
  var originY = MINI_MAP_PADDING + ((innerHeight - scaledHeight) / 2);

  return {
    boxWidth: boxWidth,
    boxHeight: boxHeight,
    minLeft: minLeft,
    minTop: minTop,
    scale: scale,
    originX: originX,
    originY: originY,
    nodes: visibleNodes
  };
}

function buildMiniMapViewport(layout, canvasRef, panPosRef, zoomRef) {
  if (!layout) return null;
  var canvas = canvasRef && canvasRef.current ? canvasRef.current : null;
  if (!canvas) return null;

  var canvasWidth = canvas.clientWidth || 0;
  var canvasHeight = canvas.clientHeight || 0;
  var zoom = zoomRef && Number.isFinite(zoomRef.current) && zoomRef.current > 0 ? zoomRef.current : 1;
  var pan = panPosRef && panPosRef.current ? panPosRef.current : { x: 0, y: 0 };

  var viewportLeft = (-pan.x) / zoom;
  var viewportTop = (-pan.y) / zoom;
  var viewportWidth = canvasWidth / zoom;
  var viewportHeight = canvasHeight / zoom;

  return {
    x: Math.round(layout.originX + ((viewportLeft - layout.minLeft) * layout.scale)),
    y: Math.round(layout.originY + ((viewportTop - layout.minTop) * layout.scale)),
    width: Math.max(0, Math.round(viewportWidth * layout.scale)),
    height: Math.max(0, Math.round(viewportHeight * layout.scale))
  };
}

function WorkflowMiniMap(props) {
  var nodes = props.nodes || [];
  var rectById = props.rectById || {};
  var canvasRef = props.canvasRef;
  var panPosRef = props.panPosRef;
  var zoomRef = props.zoomRef;
  var connections = props.connections || [];
  var onNavigate = props.onNavigate;
  var containerRef = useRef(null);
  var layoutRef = useRef(null);
  var [tick, setTick] = useState(0);
  var [boxSize, setBoxSize] = useState({ width: 0, height: 0 });

  useEffect(function() {
    var timerId = window.setInterval(function() {
      setTick(function(value) {
        return value + 1;
      });
    }, 120);

    return function() {
      window.clearInterval(timerId);
    };
  }, []);

  useEffect(function() {
    var element = containerRef.current;
    if (!element) return;

    function updateSize() {
      setBoxSize({
        width: element.clientWidth || 0,
        height: element.clientHeight || 0
      });
    }

    updateSize();

    if (typeof ResizeObserver === 'undefined') return;

    var observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return function() {
      observer.disconnect();
    };
  }, []);

  var viewportBounds = useMemo(function() {
    var canvas = canvasRef && canvasRef.current ? canvasRef.current : null;
    if (!canvas) return null;
    var canvasWidth = canvas.clientWidth || 0;
    var canvasHeight = canvas.clientHeight || 0;
    var zoom = zoomRef && Number.isFinite(zoomRef.current) && zoomRef.current > 0 ? zoomRef.current : 1;
    var pan = panPosRef && panPosRef.current ? panPosRef.current : { x: 0, y: 0 };
    var left = (-pan.x) / zoom;
    var top = (-pan.y) / zoom;
    return {
      left: left,
      top: top,
      right: left + canvasWidth / zoom,
      bottom: top + canvasHeight / zoom
    };
  }, [tick, canvasRef, panPosRef, zoomRef]);

  var layout = useMemo(function() {
    if (!boxSize.width || !boxSize.height) return null;
    return buildMiniMapLayout(nodes, rectById, boxSize.width, boxSize.height, viewportBounds);
  }, [nodes, rectById, boxSize.width, boxSize.height, viewportBounds]);

  layoutRef.current = layout;

  var handleClick = useCallback(function(event) {
    if (!onNavigate || !layoutRef.current) return;
    var lo = layoutRef.current;
    var svg = event.currentTarget;
    var rect = svg.getBoundingClientRect();
    // Convert click pixel position to SVG viewBox coordinates
    var svgX = ((event.clientX - rect.left) / rect.width) * lo.boxWidth;
    var svgY = ((event.clientY - rect.top) / rect.height) * lo.boxHeight;
    // Convert SVG coordinates to world coordinates
    var worldX = ((svgX - lo.originX) / lo.scale) + lo.minLeft;
    var worldY = ((svgY - lo.originY) / lo.scale) + lo.minTop;
    onNavigate(worldX, worldY);
  }, [onNavigate]);

  var viewport = useMemo(function() {
    return buildMiniMapViewport(layout, canvasRef, panPosRef, zoomRef);
  }, [layout, tick, canvasRef, panPosRef, zoomRef]);

  return h(
    'div',
    {
      ref: containerRef,
      className: 'canvas-minimap',
      title: 'Workflow overview',
      role: 'img',
      'aria-label': 'Workflow overview',
      style: layout ? undefined : { visibility: 'hidden' }
    },
    layout ? h(
      'svg',
      {
        className: 'canvas-minimap-svg',
        viewBox: '0 0 ' + layout.boxWidth + ' ' + layout.boxHeight,
        preserveAspectRatio: 'none',
        onClick: handleClick,
        style: { cursor: 'pointer' }
      },
      h('rect', {
        className: 'canvas-minimap-background',
        x: '0',
        y: '0',
        width: layout.boxWidth,
        height: layout.boxHeight,
        rx: '14',
        ry: '14'
      }),
      connections.map(function(conn) {
        if (!conn || !conn.from || !conn.to) return null;
        var x1 = Math.round(layout.originX + ((conn.from.x - layout.minLeft) * layout.scale));
        var y1 = Math.round(layout.originY + ((conn.from.y - layout.minTop) * layout.scale));
        var x2 = Math.round(layout.originX + ((conn.to.x - layout.minLeft) * layout.scale));
        var y2 = Math.round(layout.originY + ((conn.to.y - layout.minTop) * layout.scale));
        var isFlowing = conn.className && conn.className.indexOf('conn-line-flowing') !== -1;
        return h('line', {
          key: 'mini-conn-' + conn.id,
          x1: x1, y1: y1, x2: x2, y2: y2,
          className: 'canvas-minimap-connection' + (isFlowing ? ' canvas-minimap-connection-flowing' : '')
        });
      }),
      layout.nodes.map(function(node) {
        var nodeWidth = Math.max(4, Math.round((node.right - node.left) * layout.scale));
        var nodeHeight = Math.max(4, Math.round((node.bottom - node.top) * layout.scale));
        var color = iconColorMap[node.icon] || '#6f8fb2';
        return h('rect', {
          key: 'mini-node-' + node.id,
          className: 'canvas-minimap-node',
          x: Math.round(layout.originX + ((node.left - layout.minLeft) * layout.scale)),
          y: Math.round(layout.originY + ((node.top - layout.minTop) * layout.scale)),
          width: nodeWidth,
          height: nodeHeight,
          rx: '3',
          ry: '3',
          style: { fill: color + '30', stroke: color }
        });
      }),
      viewport
        ? h('rect', {
            className: 'canvas-minimap-viewport',
            x: viewport.x,
            y: viewport.y,
            width: viewport.width,
            height: viewport.height,
            rx: '5',
            ry: '5'
          })
        : null
    ) : null
  );
}

module.exports = WorkflowMiniMap;
